import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiBox, FiPlus, FiEdit2, FiTrash2, FiSearch, FiCheck,
  FiAlertTriangle
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';
const ITEMS_PER_PAGE = 20;

// Helper function to remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

const SuppliesListView = ({ onAdd, onEdit, refreshKey }) => {
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const observerTarget = useRef(null);
  const searchDebounceRef = useRef(null);
  const isFirstRender = useRef(true);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [longPressTimer, setLongPressTimer] = useState(null);
  
  // Delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const unitLabels = { piece: 'Cái', kg: 'Kg', bag: 'Túi' };

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load supplies with pagination
  const loadSupplies = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        skipCount: 'true'
      };

      if (debouncedSearch) params.search = debouncedSearch;

      const response = await axios.get(`${API_URL}/supplies`, { params });
      const { data, pagination } = response.data;

      if (reset || pageNum === 1) {
        setSupplies(data || []);
      } else {
        setSupplies(prev => [...prev, ...(data || [])]);
      }

      setPage(pageNum);
      setHasMore(pagination?.hasMore || false);
    } catch (error) {
      toast.error('Không thể tải danh sách vật dụng');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadSupplies(page + 1, false);
  }, [hasMore, loadingMore, page, loadSupplies]);

  // Initial load
  useEffect(() => {
    loadSupplies(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Reload when search changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadSupplies(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, loadingMore, loading, loadMoreItems]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/supplies/${id}`);
      setSupplies(prev => prev.filter(s => s._id !== id));
      setShowDeleteConfirm(null);
    } catch (error) {
      toast.error('Không thể xóa vật dụng');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/supplies/${id}`)));
      setSupplies(prev => prev.filter(s => !selectedIds.includes(s._id)));
      setSelectedIds([]);
      setShowDeleteConfirm(null);
    } catch (error) {
      toast.error('Không thể xóa vật dụng');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredSupplies.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSupplies.map(s => s._id));
    }
  };

  // Long press for mobile bulk select
  const handleTouchStart = (id) => {
    const timer = setTimeout(() => {
      if (!selectedIds.includes(id)) {
        setSelectedIds([id]);
      }
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const getTotalQuantity = (supply) => {
    if (supply.options && supply.options.length > 0) {
      return supply.options.reduce((sum, opt) => sum + (opt.quantity || 0), 0);
    }
    return supply.quantity || 0;
  };

  const isLowStock = (supply) => {
    const total = getTotalQuantity(supply);
    return total <= (supply.minStock || 0);
  };

  const filteredSupplies = supplies.filter(s => {
    if (!debouncedSearch) return true;
    const searchLower = debouncedSearch.toLowerCase();
    const searchNormalized = removeVietnameseDiacritics(debouncedSearch);
    const nameLower = (s.name || '').toLowerCase();
    const nameNormalized = removeVietnameseDiacritics(s.name);
    return nameLower.includes(searchLower) || nameNormalized.includes(searchNormalized);
  });

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FiBox className="text-green-600" />
          Danh sách vật dụng
        </h2>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm vật dụng..."
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <button
            onClick={() => onAdd()}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <FiPlus /> {!isMobile && 'Thêm'}
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-medium text-blue-700">{selectedIds.length} đã chọn</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Bỏ chọn
            </button>
          </div>
          <button
            onClick={() => setShowDeleteConfirm({ bulk: true })}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FiTrash2 size={16} /> Xóa
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : filteredSupplies.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <FiBox size={48} className="mb-2 opacity-50" />
          <p>Chưa có vật dụng nào</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Desktop Table */}
          {!isMobile ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === filteredSupplies.length && filteredSupplies.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên vật dụng</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Đơn vị</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Options</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tồn kho</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSupplies.map((supply) => (
                    <tr key={supply._id} className={`hover:bg-gray-50 ${selectedIds.includes(supply._id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(supply._id)}
                          onChange={() => toggleSelect(supply._id)}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-medium">{supply.name}</span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {unitLabels[supply.unit] || supply.unit}
                        {supply.unit !== 'piece' && supply.piecesPerUnit > 1 && (
                          <span className="text-gray-400 ml-1">
                            (1 {unitLabels[supply.unit]} = {supply.piecesPerUnit} cái)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {supply.options && supply.options.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {supply.options.map((opt, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                                {opt.name}: {opt.quantity}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right font-medium">
                        {getTotalQuantity(supply)} {unitLabels[supply.unit]}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {isLowStock(supply) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                            <FiAlertTriangle size={12} /> Sắp hết
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            Còn hàng
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => onEdit(supply)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Sửa"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm({ id: supply._id, name: supply.name })}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Xóa"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                </div>
              )}
              
              {/* Load more trigger */}
              {!loadingMore && hasMore && (
                <div ref={observerTarget} className="h-10" />
              )}
            </div>
          ) : (
            /* Mobile Cards */
            <div className="space-y-2">
              {filteredSupplies.map((supply) => (
                <div
                  key={supply._id}
                  onTouchStart={() => handleTouchStart(supply._id)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchEnd}
                  className={`bg-white border rounded-xl p-3 ${selectedIds.includes(supply._id) ? 'border-green-500 bg-green-50' : ''}`}
                  onClick={() => {
                    if (selectedIds.length > 0) {
                      toggleSelect(supply._id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {selectedIds.length > 0 && (
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedIds.includes(supply._id) ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                            {selectedIds.includes(supply._id) && <FiCheck className="text-white" size={12} />}
                          </div>
                        )}
                        <span className="font-medium">{supply.name}</span>
                        {isLowStock(supply) && (
                          <FiAlertTriangle className="text-red-500" size={14} />
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>Tồn: <strong className={isLowStock(supply) ? 'text-red-600' : 'text-green-600'}>
                          {getTotalQuantity(supply)} {unitLabels[supply.unit]}
                        </strong></span>
                      </div>
                      {supply.options && supply.options.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {supply.options.map((opt, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                              {opt.name}: {opt.quantity}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedIds.length === 0 && (
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(supply); }}
                          className="p-2 text-blue-600"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm({ id: supply._id, name: supply.name }); }}
                          className="p-2 text-red-500"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                </div>
              )}
              
              {/* Load more trigger */}
              {!loadingMore && hasMore && (
                <div ref={observerTarget} className="h-10" />
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-4">
            <h3 className="font-semibold mb-2">
              {showDeleteConfirm.bulk 
                ? `Xóa ${selectedIds.length} vật dụng?` 
                : `Xóa "${showDeleteConfirm.name}"?`}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Hành động này không thể hoàn tác.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={() => showDeleteConfirm.bulk ? handleBulkDelete() : handleDelete(showDeleteConfirm.id)}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliesListView;
